<!--
File: BridgesTable.vue
Description: show list of bridges entered in the DB.
-->
<template>
  <div class="md-layout">
    <md-card>
      <md-card-content>
        <md-table :value="queriedData" @md-selected="onSelect" :md-sort.sync="currentSort"
          :md-sort-order.sync="currentSortOrder"
          class="paginated-table table-striped table-hover">
          <md-table-toolbar style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
            <md-field>
              <label for="pages">{{ $t('label.per_page') }}</label>
              <md-select v-model="pagination.perPage" name="pages">
                <md-option v-for="item in pagination.perPageOptions" :key="item" :label="item" :value="item">
                  {{ item }}
                </md-option>
              </md-select>
            </md-field>
            <!-- Search + Export Group -->
            <div style="display: flex; align-items: center; gap: 10px;">
              <md-field>
                <md-input type="search" class="mb-3" clearable style="width: 200px"
                  :placeholder="`${$t('label.search_records')}`" v-model="searchQuery"></md-input>
              </md-field>

              <md-button v-show="isAddBtnAllowed" class="md-raised md-success" @click="editBridge(null)">
                {{ $t('buttons.add') }}
              </md-button>

              <md-button class="md-success" @click="excelExport">
                {{ $t('buttons.excel_export') }}
              </md-button>
            </div>
          </md-table-toolbar>

          <md-table-row slot="md-table-row" slot-scope="{item}">
            <md-table-cell>
              <a href="#" @click.stop.prevent="passportOpen(item.bridge_uuid)" :TITLE="$t('label.passport')">
                <md-icon>assignment</md-icon>
              </a>
              <a href="#" @click.stop.prevent="carouselShow(item.bridge_uuid)" :TITLE="$t('label.show_pictures')">
                <md-icon>image</md-icon>
              </a>
            </md-table-cell>
            <md-table-cell v-for="title in bridgesTitles" :key="title" :md-label="$t(`bridges_table.${title}`)"
              :md-sort-by="title">
              {{ item[title] }}
            </md-table-cell>
            <!-- Actions column -->
              <md-table-cell :md-label="$t('stdCols.actions')" class="actions-column">
                <div class="cell-actions">
                  <TableActions
                    :btnEditAllowed="isEditBtnAllowed"
                    :btnDeleteAllowed="isDeleteBtnAllowed"
                    :editTooltip="$t('screen_titles.bridge_upd')"
                    :deleteTooltip="$t('screen_titles.bridge_delete')"
                    @edit="editBridge(item)"
                    @delete="deleteBridge(item)"
                  />
                </div>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>

      <!-- BridgesTable.vue -->
      <BridgesTableEditForm v-if="showEditForm" :bridge="selectedBridge"
        :bridgeId="selectedBridge ? selectedBridge.bridge_uuid : null" @close="toggleEditForm(false)"
        @saved="onBridgeSaved" />

      <md-card-actions md-alignment="space-between">
        <div>
          <p class="card-category">
            {{
              $t('label.showing_from_to_of_entries', {
                from: to === 0 ? 0 : from + 1,
                to: to,
                total
              })
            }}
          </p>
        </div>
        <pagination class="pagination-no-border pagination-success" v-model="pagination.currentPage"
          :per-page="pagination.perPage" :total="total"></pagination>
      </md-card-actions>
    </md-card>
    <div class="carousel-block" v-if="isCarouselShow">
      <div class="close-carousel" @click="carouselHide">
        <md-icon>clear</md-icon>
      </div>
      <el-carousel>
        <el-carousel-item v-for="item in carouselImages" :key="item">
          <img :src="item" alt="" class="carousel-block-image">
        </el-carousel-item>
      </el-carousel>
    </div>
  </div>
</template>

<script>
import { Pagination } from '@/components'
import { mapState } from 'vuex'
import permissions from "@/mixins/permissionsMixin"
import Fuse from 'fuse.js'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import BridgesTableEditForm from './BridgesTableEditForm.vue'
import { TableActions } from '@/pages/Components'
import { deleteConfirmation, deletedMessage, errorMessage } from '@/mixins/messagesMixin'

export default {
  name: 'BridgesTable',
  mixins: [permissions],
  components: {
    Pagination,
    BridgesTableEditForm,
    TableActions
  },
  data() {
    return {
      formName: 'BridgesTable',

      carouselImages: [],
      bridgesList: [],
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      currentSort: 'region_name',
      currentSortOrder: 'asc',
      searchQuery: '',
      selected_bridges: [],
      isCarouselShow: false,
      showEditForm: false,
      selectedBridge: null,
    }
  },


  mounted() {
    // Check if we are eligible to view the form
    if (!this.checkIfScreenAllowed()) {
      this.onClose()
      return
    };

    this.$store.dispatch('LOAD_BRIDGES_TABLE_DATA').then(() => {
      this.fuseSearch = new Fuse(this.bridgesData, {
        keys: [...this.bridgesTitles],
        threshold: 0.3
      })
    })
  },

  watch: {
    searchQuery(value) {
      let result = this.bridgesData
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery)
      }
      this.searchedData = result
    }
  },

  computed: {
    ...mapState({
      bridgesTable: (state) => state.Bridges.bridges_table
    }),

    isAddBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'AddButton');
    },

    isEditBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'EditButton');
    },

    isDeleteBtnAllowed() {
      return this.isAllowed(this.formName, 'Button', 'DeleteButton');
    },

    bridgesData() {
      return this.bridgesTable.data
    },

    bridgesTitles() {
      return this.bridgesTable.vars
    },

    queriedData() {
      const result = !this.searchQuery
        ? this.tableData
        : this.searchedData
          ? this.searchedData
          : []
      return result.slice(this.from, this.to)
    },

    to() {
      let highBound = this.from + this.pagination.perPage
      if (this.total < highBound) {
        highBound = this.total
      }
      return highBound
    },

    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1)
    },

    total() {
      return this.searchQuery ? this.searchedData.length : this.tableData.length
    },

    tableData() {
      // Apply sorting directly in the computed property instead of relying on MD Table's sort function
      const data = [...this.bridgesData]; // Create a copy to avoid mutating original data
      
      if (this.currentSort && data.length > 0) {
        return data.sort((a, b) => {
          const sortBy = this.currentSort;
          
          // Get values and convert to string for comparison
          const aVal = a[sortBy] ? a[sortBy].toString() : '';
          const bVal = b[sortBy] ? b[sortBy].toString() : '';
          
          // Primary sort comparison
          let primaryComparison;
          if (this.currentSortOrder === 'desc') {
            primaryComparison = bVal.localeCompare(aVal, undefined, { numeric: true });
          } else {
            primaryComparison = aVal.localeCompare(bVal, undefined, { numeric: true });
          }
          
          // If primary sort values are equal, use bridge_uuid as secondary sort for stability
          if (primaryComparison === 0) {
            return a.bridge_uuid.localeCompare(b.bridge_uuid);
          }
          
          return primaryComparison;
        });
      }
      
      return data;
    },

    getColumns() {
      return [
        { header: this.$t('bridges_table.region_name'), key: 'region_name', width: 20 },
        { header: this.$t('bridges_table.pluad_uad'), key: 'pluad_uad', width: 18 },
        { header: this.$t('bridges_table.dep'), key: 'dep', width: 16 },
        { header: this.$t('bridges_table.route_name'), key: 'route_name', width: 50 },
        { header: this.$t('bridges_table.location'), key: 'location', width: 8 },
        { header: this.$t('bridges_table.structural_soundness'), key: 'structural_soundness', width: 18, num: true, digits: 0 },
        { header: this.$t('bridges_table.maintenance_category'), key: 'maintenance_category', width: 20 },
        { header: this.$t('bridges_table.dim_length'), key: 'dim_length', width: 15, num: true, digits: 0 },
        { header: this.$t('bridges_table.bridge_length_count'), key: 'bridge_length_count', width: 12, num: true, digits: 0 },
        { header: this.$t('bridges_table.dimension_width_roadway'), key: 'dim_width_roadway', width: 16, num: true, digits: 0 },
        { header: this.$t('bridges_table.dim_width_sidewalk'), key: 'dim_width_sidewalk', width: 16, num: true, digits: 0 },
        { header: this.$t('bridges_table.num_of_span'), key: 'num_of_span', width: 11, num: true, digits: 0 },
        { header: this.$t('bridges_table.sub_height_of_substructure'), key: 'sub_height_of_substructure', width: 20, num: true, digits: 0 },
        { header: this.$t('bridges_table.sub_num_of_substructure'), key: 'sub_num_of_substructure', width: 18, num: true, digits: 0 },
        { header: this.$t('bridges_table.bearing_with_or_without'), key: 'bearing_with_or_without', width: 21, num: true, digits: 0 },
        { header: this.$t('bridges_table.year_construction'), key: 'year_construction', width: 16, num: true, digits: 0 },
        { header: this.$t('bridges_table.year_last_repair'), key: 'year_last_repair', width: 14, num: true, digits: 0 },
      ]
    }
  },

  methods: {
    passportOpen(uuid) {
      this.$router.push({ name: "BridgesPassport", params: { uuid: uuid, target: 'bridge' } })
    },

    carouselHide() {
      this.isCarouselShow = false
    },

    editBridge(item) {
      this.selectedBridge = item ? { ...item } : {};
      this.toggleEditForm(true);
    },

    toggleEditForm(state) {
      this.showEditForm = state;
    },

    onBridgeSaved() {
      // Store current sort settings to preserve user's choice
      const preserveSort = this.currentSort;
      const preserveOrder = this.currentSortOrder;
      
      this.toggleEditForm(false);
      
      // Reload grid data and restore sort (computed property will handle the actual sorting)
      this.$store.dispatch('LOAD_BRIDGES_TABLE_DATA').then(() => {
        // Restore user's sort preference - the tableData computed property will automatically sort
        this.currentSort = preserveSort;
        this.currentSortOrder = preserveOrder;
      });
    },

    // Removed complex wasEditedFieldSorted function - keeping it simple

    async excelExport() {
      // Create new Excel file
      const workbook = new ExcelJS.Workbook();
      this.fillWorkSheet(workbook, this.$t('map.bridges'))

      // Save
      const fileName = `${this.tableData[0].region_name}.xlsx`
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, fileName);
    },

    fillWorkSheet(workbook, workbookName) {
      const headerCellFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0070C0' } } // Blue
      const worksheet = workbook.addWorksheet(workbookName);

      console.log('this.bridgesTitles =', this.bridgesTitles);
      console.log('this.tableData =', this.tableData);

      // Set the table headers
      worksheet.columns = this.getColumns;

      // Apply formatting to the table headers
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell({ includeEmpty: true }, (cell) => { cell.fill = headerCellFill })
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Number formatting for the columns
      this.getColumns.forEach(item => {
        if (item.num)
          worksheet.getColumn(item.key).numFmt = item.digits == 2 ? '0.00' : '0'
      })

      // Fill rows from tableData
      this.tableData.forEach(item => {
        worksheet.addRow(item);
      });
    },

    carouselShow(uuid) {
      this.carouselImages = []
      const path = '/bridges_tunnels_images/Bridges/'
      const img_overview = `${path}overview/${uuid.toUpperCase()}.jpeg`
      const img_surface = `${path}surface/${uuid.toUpperCase()}.jpeg`
      const img_underside = `${path}underside/${uuid.toUpperCase()}.jpeg`
      this.carouselImages = [img_overview, img_surface, img_underside]
      this.isCarouselShow = true
    },

    // Removed customSort method - sorting now handled directly in tableData computed property

    onSelect(items) {
      this.selected_bridges = items
    },

    async deleteBridge(item) {
      // Ask for confirmation
      const { isConfirmed } = await deleteConfirmation(item.route_name || item.bridge_uuid)
      if (!isConfirmed) return

      // Call store action to delete. apiCall/handleApiCall returns data or throws on error.
      try {
        await this.$store.dispatch('DELETE_BRIDGE', item.bridge_uuid)
        await deletedMessage(item.route_name || item.bridge_uuid)
        // Reload table data
        await this.$store.dispatch('LOAD_BRIDGES_TABLE_DATA')
      } catch (err) {
        await errorMessage(err.message || this.$t('errors.unknown'))
      }
    }
  }
}
</script>

<style lang="scss">
.md-card {
  margin: 0px 0;
  height: 100%;
}

.el-carousel__container {
  position: relative;
  height: 40em;
}

.el-carousel__item {
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-height: 100%;
    width: auto !important;
  }

  h3 {
    color: #475669;
    font-size: 14px;
    opacity: 0.75;
    line-height: 150px;
    margin: 0;
  }
}

.el-carousel__item:nth-child(2n) {
  background-color: #99a9bf;
}

.el-carousel__item:nth-child(2n+1) {
  background-color: #d3dce6;
}

.carousel-block {
  position: absolute;
  width: 99.5%;
  z-index: 100;

  .close-carousel {
    position: absolute;
    top: 3px;
    right: 3px;
    z-index: 101;
    cursor: pointer;
  }
}

/* Center the actions column content */
.actions-column {
  text-align: left !important;
}
.cell-actions {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

/* Ensure consistent spacing between action icons (edit/delete) */
.cell-actions > *:not(:first-child) {
  margin-left: 8px !important;
}
</style>
